import { http } from '@/api/http';
import type { CareTeamMemberDetailViewModel } from '@/models/patient';

/**
 * Care-team endpoints + tenant catalogs.
 *
 * Two separate concerns share this slice:
 *   - the live `/persons/{guid}/care-team` resource (PTV2-1095)
 *   - the tenant-scoped catalogs (`/tenant/programs`, `/tenant/users`) used by
 *     pickers in the editor. Local stubs until ai-hub publishes the contract.
 *
 * The per-member endpoints work against `CareTeamMember.id` (the membership
 * row's GUID) — distinct from `userId` which references the system user. The
 * primary-clinician endpoints, by contrast, key on `userId`.
 */

export interface TenantProgramDto {
  readonly id: string;
  readonly name: string;
}

export interface TenantUserDto {
  readonly guid: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly role: string;
  readonly title: string | null;
}

/**
 * Shape of `CareTeamMemberDTO` as returned by the live BE (mirrors the
 * OpenAPI spec; the BE may extend with a resolved display name later, so we
 * keep `fullName` / `display_name` as permissive reads in the mapper).
 */
export interface CareTeamMemberRowDto {
  readonly id?: string;
  readonly user_id?: string;
  readonly role?: string;
  readonly added_at?: string;
  readonly added_by?: string;
  readonly is_primary_clinician?: boolean;
}

/** Body for `POST /persons/{guid}/care-team/members`. */
export interface AddCareTeamMemberBody {
  readonly user_id: string;
  readonly role: string;
}

/** Single op in the RFC 6902 patch body for role edits. */
interface JsonPatchReplaceRoleOp {
  readonly op: 'replace';
  readonly path: '/role';
  readonly value: string;
}

export const careTeamApi = {
  /** GET /tenant/programs — catalog of programs available for assignment (local stub). */
  listTenantPrograms: (
    options?: { signal?: AbortSignal },
  ): Promise<TenantProgramDto[]> =>
    http.get<TenantProgramDto[]>('/tenant/programs', options),

  /**
   * GET /tenant/users — directory of tenant users for picker UIs (local stub).
   * Optional `role` filter narrows to e.g. genetic counselors;
   * optional `q` performs a server-side substring search.
   */
  listTenantUsers: (
    params: { role?: string; q?: string } = {},
    options?: { signal?: AbortSignal },
  ): Promise<TenantUserDto[]> =>
    http.get<TenantUserDto[]>('/tenant/users', {
      ...options,
      query: {
        ...(params.role !== undefined ? { role: params.role } : {}),
        ...(params.q !== undefined ? { q: params.q } : {}),
      },
    }),

  /** GET /persons/{guid}/care-team — live member rows in add-order. */
  getCareTeam: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<readonly CareTeamMemberRowDto[]> =>
    http.get<readonly CareTeamMemberRowDto[]>(
      `/persons/${encodeURIComponent(guid)}/care-team`,
      options,
    ),

  /** POST /persons/{guid}/care-team/members — add a member; returns the created row. */
  addCareTeamMember: (
    guid: string,
    body: AddCareTeamMemberBody,
    options?: { signal?: AbortSignal },
  ): Promise<CareTeamMemberRowDto> =>
    http.post<CareTeamMemberRowDto>(
      `/persons/${encodeURIComponent(guid)}/care-team/members`,
      body,
      options,
    ),

  /** DELETE /persons/{guid}/care-team/members/{memberId} — soft-delete a member. */
  removeCareTeamMember: (
    guid: string,
    memberId: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/care-team/members/${encodeURIComponent(memberId)}`,
      options,
    ),

  /**
   * PATCH /persons/{guid}/care-team/members/{memberId} — RFC 6902 JSON-Patch
   * limited to the `/role` field. Wrapped here so callers pass a flat
   * `{ role }` argument instead of hand-rolling the operation array.
   */
  updateCareTeamMemberRole: (
    guid: string,
    memberId: string,
    body: { readonly role: string },
    options?: { signal?: AbortSignal },
  ): Promise<CareTeamMemberRowDto> => {
    const ops: readonly JsonPatchReplaceRoleOp[] = [
      { op: 'replace', path: '/role', value: body.role },
    ];
    return http.patch<CareTeamMemberRowDto>(
      `/persons/${encodeURIComponent(guid)}/care-team/members/${encodeURIComponent(memberId)}`,
      ops,
      {
        ...options,
        headers: { 'content-type': 'application/json-patch+json' },
      },
    );
  },

  /** PUT /persons/{guid}/care-team/primary/{userId} — promote a member to primary. */
  setPrimaryClinician: (
    guid: string,
    userId: string,
    options?: { signal?: AbortSignal },
  ): Promise<CareTeamMemberRowDto> =>
    http.put<CareTeamMemberRowDto>(
      `/persons/${encodeURIComponent(guid)}/care-team/primary/${encodeURIComponent(userId)}`,
      undefined,
      options,
    ),

  /** DELETE /persons/{guid}/care-team/primary — clear the primary, leaving the member on the team. */
  clearPrimaryClinician: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/care-team/primary`,
      options,
    ),
};

/**
 * Maps a raw row from `GET /persons/{guid}/care-team` to the panel's
 * view-model. The BE schema strictly types only `userId`/`role`, but some
 * deployments echo a resolved name back — read those permissively as
 * `display_name` or `full_name` so the UI doesn't have to spawn a per-row
 * `/staff/{guid}` lookup when the server already did the join.
 */
export function mapCareTeamMemberRow(
  dto: CareTeamMemberRowDto,
): CareTeamMemberDetailViewModel | null {
  if (typeof dto.id !== 'string' || dto.id.length === 0) return null;
  if (typeof dto.user_id !== 'string' || dto.user_id.length === 0) return null;
  const extras = dto as CareTeamMemberRowDto & {
    readonly display_name?: string | null;
    readonly full_name?: string | null;
    readonly fullName?: string | null;
  };
  const resolvedName =
    nonEmpty(extras.display_name) ?? nonEmpty(extras.full_name) ?? nonEmpty(extras.fullName);
  return {
    id: dto.id,
    userId: dto.user_id,
    displayName: resolvedName,
    role: typeof dto.role === 'string' ? dto.role : '',
    isPrimary: dto.is_primary_clinician === true,
    addedAt: typeof dto.added_at === 'string' ? dto.added_at : null,
    addedBy: typeof dto.added_by === 'string' ? dto.added_by : null,
  };
}

function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
